/**
 * โพล์ตรวจ session กับ user-auth เป็นระยะ
 * - 401 SESSION_REPLACED → handleSessionReplaced → /login
 * - 401 อื่น / token หมดอายุ → ลอง refresh แล้วถ้าไม่ได้ → handleAuthExpired → /login
 */
import { useEffect, useRef } from 'react'
import * as authService from '../services/authService'
import { handleAuthExpired, handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

/** ระยะเวลา poll ปกติ (tab active) */
const POLL_INTERVAL_MS = 60_000
/** ขั้นต่ำห่างกันระหว่าง request (ป้องกัน multi-instance ยิงซ้อน) */
const MIN_PROFILE_INTERVAL_MS = 55_000
/** เมื่อได้ 429 ไม่ยิงซ้ำก่อนครบช่วงนี้ */
const BACKOFF_AFTER_429_MS = 120_000
const PROFILE_URL = '/api/v1/user-auth/profile'

// Module-level throttle — ใช้ร่วมกันทุก instance ของ hook นี้
let lastProfileCheckAt = 0
let backoffUntil = 0

export function useSessionReplacedPoll(enabled: boolean) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const recoverAuth = async () => {
      const refreshed = await authService.refreshToken()
      if (refreshed.success) return
      await handleAuthExpired()
    }

    const checkSession = async () => {
      if (document.visibilityState === 'hidden') return

      const token = authService.getAccessToken()
      const tenantId = getRuntimeTenantIdSync() || ''

      // access หมด/หาย แต่ยังมี refresh → ลองต่ออายุ; ถ้าไม่ได้ค่อยพาไป login
      if (!token || !authService.isAuthenticated()) {
        await recoverAuth()
        return
      }

      const now = Date.now()
      if (now < backoffUntil) return
      if (now - lastProfileCheckAt < MIN_PROFILE_INTERVAL_MS) return
      lastProfileCheckAt = now

      try {
        const res = await fetch(withApiBaseUrl(PROFILE_URL), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Tenant-ID': tenantId,
          },
          credentials: 'include',
        })
        if (res.status === 429) {
          backoffUntil = Date.now() + BACKOFF_AFTER_429_MS
          return
        }
        if (res.status === 401) {
          const data = (await res.json().catch(() => ({}))) as { code?: string }
          if (data?.code === 'SESSION_REPLACED') {
            await handleSessionReplaced()
            return
          }
          await recoverAuth()
        }
      } catch {
        // ignore network errors; next poll will retry
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkSession()
    }

    void checkSession()
    intervalRef.current = setInterval(() => {
      void checkSession()
    }, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled])
}
